import { useEffect, useState } from "react";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { EntityCollection } from "../../api/OasisBackendApi";
import Plot from "react-plotly.js";

type StatsByK = {
    [k: string]: { mean: number | null; median: number | null; stdev: number | null; count: number };
};

type ComparisonsByK = {
    [k: string]: { semantic_better: number; oasis_better: number; ties: number; compared_count: number };
};

type BenchmarkSummary = {
    entity_collection_id: string;
    dimensions: string[];
    queries_used: string[];
    semantic_stats: StatsByK;
    oasis_stats: StatsByK;
    comparisons: ComparisonsByK;
    metadata: { semantic_scores?: { [k: string]: number[] }; oasis_scores?: { [k: string]: number[] } };
};

const StatRow = ({ label, value }: { label: string; value: string | number | null }) => (
    <div className="flex justify-between text-sm">
        <span className="text-gray-400">{label}</span>
        <span className="text-gray-200">{value ?? "-"}</span>
    </div>
);

const BenchmarkView = ({ Database_id, isOwner }: { Database_id: string; isOwner?: boolean }) => {
    const db = useOasisBackend();
    const [entityCollection, setEntityCollection] = useState<EntityCollection | null>(null);
    const [loadingEC, setLoadingEC] = useState(false);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<BenchmarkSummary | null>(null);
    const [limit, setLimit] = useState<number>(50);

    const fetchEntityCollection = async () => {
        try {
            setLoadingEC(true);
            setError(null);
            const response = await db.endpoints.api.apiDatabaseEntityCollectionRetrieve(Database_id);
            setEntityCollection(response.data);
        } catch (err) {
            console.error("Error fetching entity collection:", err);
            setError("Failed to load entity collection");
        } finally {
            setLoadingEC(false);
        }
    };

    useEffect(() => {
        fetchEntityCollection();
    }, [Database_id]);

    const runBenchmark = async () => {
        if (!entityCollection?.id) return;
        try {
            setRunning(true);
            setError(null);
            const { data } = await db.endpoints.api.apiEntityCollectionBenchmarkRetrieve(entityCollection.id, { limit });
            setSummary(data as BenchmarkSummary);
        } catch (err: any) {
            console.error("Benchmark error:", err);
            setError("Failed to run benchmark");
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="flex flex-col gap-4 p-4 sm:p-8">
            <div className="flex items-center gap-2">
                <h2 className="font-bold text-lg">Benchmark</h2>
                {loadingEC && <span className="text-sm text-gray-400">Loading entity collection…</span>}
            </div>

            {error && <div className="text-red-400 text-sm">{error}</div>}

            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                <div className="flex flex-col">
                    <label className="text-xs text-gray-400 mb-1">Top-K limit</label>
                    <input
                        type="number"
                        min={1}
                        max={200}
                        value={limit}
                        onChange={(e) => setLimit(parseInt(e.target.value || '50'))}
                        className="px-3 py-2 rounded-md bg-gray-800 text-white border border-gray-700 w-32"
                    />
                </div>
                <button
                    className={`px-3 py-2 rounded-md ${running ? 'bg-gray-600' : 'bg-gray-400 hover:bg-gray-500'}`}
                    onClick={runBenchmark}
                    disabled={running || !entityCollection?.id}
                >
                    {running ? 'Running…' : 'Run Benchmark'}
                </button>
            </div>

            {summary && (() => {
                // Extract k values and sort them
                const kValues = Object.keys(summary.semantic_stats)
                    .map(k => parseInt(k))
                    .filter(k => !isNaN(k))
                    .sort((a, b) => a - b);

                // Prepare chart data
                const meanData = {
                    x: kValues,
                    semantic: kValues.map(k => summary.semantic_stats[k.toString()]?.mean ?? null),
                    oasis: kValues.map(k => summary.oasis_stats[k.toString()]?.mean ?? null),
                };

                const medianData = {
                    x: kValues,
                    semantic: kValues.map(k => summary.semantic_stats[k.toString()]?.median ?? null),
                    oasis: kValues.map(k => summary.oasis_stats[k.toString()]?.median ?? null),
                };

                const stdevData = {
                    x: kValues,
                    semantic: kValues.map(k => summary.semantic_stats[k.toString()]?.stdev ?? null),
                    oasis: kValues.map(k => summary.oasis_stats[k.toString()]?.stdev ?? null),
                };

                const comparisonData = {
                    k: kValues.map(k => k.toString()),
                    semantic_better: kValues.map(k => summary.comparisons[k.toString()]?.semantic_better ?? 0),
                    oasis_better: kValues.map(k => summary.comparisons[k.toString()]?.oasis_better ?? 0),
                    ties: kValues.map(k => summary.comparisons[k.toString()]?.ties ?? 0),
                };

                return (
                    <div className="flex flex-col gap-4">
                        {/* Charts Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Mean Scores Chart */}
                            <div className="bg-gray-800 p-4 rounded-md">
                                <h3 className="font-semibold mb-4">Mean Scores by K</h3>
                                <div className="h-80">
                                    <Plot
                                        data={[
                                            {
                                                x: meanData.x,
                                                y: meanData.semantic,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Semantic',
                                                marker: { color: '#60a5fa' },
                                                line: { color: '#60a5fa', shape: 'spline' }
                                            },
                                            {
                                                x: meanData.x,
                                                y: meanData.oasis,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Oasis',
                                                marker: { color: '#f472b6' },
                                                line: { color: '#f472b6', shape: 'spline' }
                                            }
                                        ]}
                                        layout={{
                                            xaxis: { title: 'K (Number of Results)' },
                                            yaxis: { title: 'Mean Score' },
                                            margin: { t: 10, b: 40, l: 50, r: 10 },
                                            showlegend: true,
                                            legend: { x: 0, y: 1 },
                                            paper_bgcolor: 'rgba(0,0,0,0)',
                                            plot_bgcolor: 'rgba(0,0,0,0)',
                                            font: { color: '#e5e7eb' }
                                        }}
                                        config={{ 
                                            responsive: true, 
                                            displayModeBar: true,
                                            modeBarButtonsToRemove: ['lasso2d', 'select2d'],
                                            displaylogo: false
                                        }}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Median Scores Chart */}
                            <div className="bg-gray-800 p-4 rounded-md">
                                <h3 className="font-semibold mb-4">Median Scores by K</h3>
                                <div className="h-80">
                                    <Plot
                                        data={[
                                            {
                                                x: medianData.x,
                                                y: medianData.semantic,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Semantic',
                                                marker: { color: '#60a5fa' },
                                                line: { color: '#60a5fa', shape: 'spline' }
                                            },
                                            {
                                                x: medianData.x,
                                                y: medianData.oasis,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Oasis',
                                                marker: { color: '#f472b6' },
                                                line: { color: '#f472b6', shape: 'spline' }
                                            }
                                        ]}
                                        layout={{
                                            xaxis: { title: 'K (Number of Results)' },
                                            yaxis: { title: 'Median Score' },
                                            margin: { t: 10, b: 40, l: 50, r: 10 },
                                            showlegend: true,
                                            legend: { x: 0, y: 1 },
                                            paper_bgcolor: 'rgba(0,0,0,0)',
                                            plot_bgcolor: 'rgba(0,0,0,0)',
                                            font: { color: '#e5e7eb' }
                                        }}
                                        config={{ 
                                            responsive: true, 
                                            displayModeBar: true,
                                            modeBarButtonsToRemove: ['lasso2d', 'select2d'],
                                            displaylogo: false
                                        }}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Standard Deviation Chart */}
                            <div className="bg-gray-800 p-4 rounded-md">
                                <h3 className="font-semibold mb-4">Standard Deviation by K</h3>
                                <div className="h-80">
                                    <Plot
                                        data={[
                                            {
                                                x: stdevData.x,
                                                y: stdevData.semantic,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Semantic',
                                                marker: { color: '#60a5fa' },
                                                line: { color: '#60a5fa', shape: 'spline' }
                                            },
                                            {
                                                x: stdevData.x,
                                                y: stdevData.oasis,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Oasis',
                                                marker: { color: '#f472b6' },
                                                line: { color: '#f472b6', shape: 'spline' }
                                            }
                                        ]}
                                        layout={{
                                            xaxis: { title: 'K (Number of Results)' },
                                            yaxis: { title: 'Standard Deviation' },
                                            margin: { t: 10, b: 40, l: 50, r: 10 },
                                            showlegend: true,
                                            legend: { x: 0, y: 1 },
                                            paper_bgcolor: 'rgba(0,0,0,0)',
                                            plot_bgcolor: 'rgba(0,0,0,0)',
                                            font: { color: '#e5e7eb' }
                                        }}
                                        config={{ 
                                            responsive: true, 
                                            displayModeBar: true,
                                            modeBarButtonsToRemove: ['lasso2d', 'select2d'],
                                            displaylogo: false
                                        }}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Comparisons Chart */}
                            <div className="bg-gray-800 p-4 rounded-md">
                                <h3 className="font-semibold mb-4">Comparisons by K</h3>
                                <div className="h-80">
                                    <Plot
                                        data={[
                                            {
                                                x: comparisonData.k.map(k => parseInt(k)),
                                                y: comparisonData.semantic_better,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Semantic Better',
                                                marker: { color: '#60a5fa' },
                                                line: { color: '#60a5fa', shape: 'spline' }
                                            },
                                            {
                                                x: comparisonData.k.map(k => parseInt(k)),
                                                y: comparisonData.oasis_better,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Oasis Better',
                                                marker: { color: '#f472b6' },
                                                line: { color: '#f472b6', shape: 'spline' }
                                            },
                                            {
                                                x: comparisonData.k.map(k => parseInt(k)),
                                                y: comparisonData.ties,
                                                type: 'scatter',
                                                mode: 'lines+markers',
                                                name: 'Ties',
                                                marker: { color: '#9ca3af' },
                                                line: { color: '#9ca3af', shape: 'spline' }
                                            }
                                        ]}
                                        layout={{
                                            xaxis: { title: 'K (Number of Results)' },
                                            yaxis: { title: 'Count' },
                                            margin: { t: 10, b: 40, l: 50, r: 10 },
                                            showlegend: true,
                                            legend: { x: 0, y: 1 },
                                            paper_bgcolor: 'rgba(0,0,0,0)',
                                            plot_bgcolor: 'rgba(0,0,0,0)',
                                            font: { color: '#e5e7eb' }
                                        }}
                                        config={{ 
                                            responsive: true, 
                                            displayModeBar: true,
                                            modeBarButtonsToRemove: ['lasso2d', 'select2d'],
                                            displaylogo: false
                                        }}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Stats Tables Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-800 p-4 rounded-md">
                        <h3 className="font-semibold mb-2">Dimensions Used</h3>
                        {summary.dimensions.length === 0 ? (
                            <div className="text-sm text-gray-400">No dimensions</div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {summary.dimensions.map((d) => (
                                    <span key={d} className="text-xs px-2 py-1 rounded bg-gray-700 border border-gray-600 text-gray-200">{d}</span>
                                ))}
                            </div>
                        )}
                    </div>

                            {/* Latest K Stats */}
                            {kValues.length > 0 && (() => {
                                const latestK = kValues[kValues.length - 1].toString();
                                const semanticStats = summary.semantic_stats[latestK];
                                const oasisStats = summary.oasis_stats[latestK];
                                return (
                                    <>
                    <div className="bg-gray-800 p-4 rounded-md">
                                            <h3 className="font-semibold mb-2">Semantic Stats (K={latestK})</h3>
                        <div className="space-y-1">
                                                <StatRow label="Mean" value={semanticStats?.mean?.toFixed(2) ?? null} />
                                                <StatRow label="Median" value={semanticStats?.median?.toFixed(2) ?? null} />
                                                <StatRow label="Std Dev" value={semanticStats?.stdev?.toFixed(2) ?? null} />
                                                <StatRow label="Samples" value={semanticStats?.count ?? null} />
                        </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-md">
                                            <h3 className="font-semibold mb-2">Oasis Stats (K={latestK})</h3>
                        <div className="space-y-1">
                                                <StatRow label="Mean" value={oasisStats?.mean?.toFixed(2) ?? null} />
                                                <StatRow label="Median" value={oasisStats?.median?.toFixed(2) ?? null} />
                                                <StatRow label="Std Dev" value={oasisStats?.stdev?.toFixed(2) ?? null} />
                                                <StatRow label="Samples" value={oasisStats?.count ?? null} />
                                            </div>
                        </div>
                                    </>
                                );
                            })()}
                    </div>

                        {/* Latest K Comparisons */}
                        {kValues.length > 0 && (() => {
                            const latestK = kValues[kValues.length - 1].toString();
                            const comparisons = summary.comparisons[latestK];
                            return (
                                <div className="bg-gray-800 p-4 rounded-md">
                                    <h3 className="font-semibold mb-2">Comparisons (K={latestK})</h3>
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-gray-700 rounded p-3">
                                <div className="text-xs text-gray-300">Semantic Better</div>
                                            <div className="text-lg font-bold text-gray-100">{comparisons?.semantic_better ?? 0}</div>
                            </div>
                            <div className="bg-gray-700 rounded p-3">
                                <div className="text-xs text-gray-300">Oasis Better</div>
                                            <div className="text-lg font-bold text-gray-100">{comparisons?.oasis_better ?? 0}</div>
                            </div>
                            <div className="bg-gray-700 rounded p-3">
                                <div className="text-xs text-gray-300">Ties</div>
                                            <div className="text-lg font-bold text-gray-100">{comparisons?.ties ?? 0}</div>
                            </div>
                        </div>
                                    <div className="mt-2 text-xs text-gray-400">Compared Count: {comparisons?.compared_count ?? 0}</div>
                    </div>
                            );
                        })()}

                        {/* Queries Used */}
                        <div className="bg-gray-800 p-4 rounded-md">
                        <h3 className="font-semibold mb-2">Queries Used</h3>
                        {summary.queries_used.length === 0 ? (
                            <div className="text-sm text-gray-400">No queries</div>
                        ) : (
                            <div className="space-y-1 max-h-64 overflow-auto">
                                {summary.queries_used.map((q, i) => (
                                    <div key={`${i}-${q.slice(0,10)}`} className="text-sm text-gray-200">{q}</div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                );
            })()}
        </div>
    );
};

export default BenchmarkView;


